<script setup lang="ts">
import { computed, markRaw, ref } from 'vue'

import IconSettingImage from '@/components/atoms/icons/IconSettingImage.vue'
import IconSettingLayout from '@/components/atoms/icons/IconSettingLayout.vue'
import SettingBorderRadius from '@/components/atoms/SettingBorderRadius.vue'
import SettingImageHeight from '@/components/atoms/SettingImageHeight.vue'
import SettingImageSelector from '@/components/beta-editor/settings/controls/BetaSettingImageSelector.vue'
import SettingImageWidth from '@/components/atoms/SettingImageWidth.vue'
import SettingLink from '@/components/atoms/SettingLink.vue'
import SettingMargin from '@/components/atoms/SettingMargin.vue'
import SettingPadding from '@/components/atoms/SettingPadding.vue'
import SettingWidth from '@/components/atoms/SettingWidth.vue'
import EditorSidebarLayout from '@/components/layouts/EditorSidebarLayout.vue'
import SettingsLayout from '@/components/layouts/SettingsLayout.vue'
import { useAuthStore } from '@/stores/auth'
import { useEditorBetaStore } from '@/stores/editor/beta'

const authStore = useAuthStore()
const betaStore = useEditorBetaStore()

const elementForEdit = computed(() => betaStore.elementForEdit)
const imageStyle = computed(() => elementForEdit.value?.extra?.style ?? {})
const imageStyleWidthLock = computed(() => elementForEdit.value?.extra?.style_width_lock ?? true)
const imageStyleHeightLock = computed(() => elementForEdit.value?.extra?.style_height_lock ?? true)
const imageStyleBorderRadiusLock = computed(() => elementForEdit.value?.extra?.style_border_radius_lock ?? true)
const settings = ref([
    { id: 1, name: 'image', icon: markRaw(IconSettingImage) },
    { id: 2, name: 'layout', icon: markRaw(IconSettingLayout) },
])

const onChangeWidth = (event: any) => {
    if (!elementForEdit.value) return

    if (event.target === 'desktop') elementForEdit.value.grid = event.value
    if (event.target === 'mobile') elementForEdit.value.gridMobile = event.value

    betaStore.updateElementForEdit()
}

const onChangeImageSelector = (event: any) => {
    if (!elementForEdit.value) return

    elementForEdit.value.frameContent = event.value?.content ?? null
    betaStore.updateElementForEdit()
}

const onChangeLink = (event: any) => {
    if (!elementForEdit.value) return

    if (event.target === 'link') elementForEdit.value.action = event.value
    if (event.target === 'open-in') elementForEdit.value.actionType = event.value

    betaStore.updateElementForEdit()
}

const onChangeStyle = (event: any) => {
    if (!elementForEdit.value) return

    const styleTargets: Record<string, string> = {
        width: 'width',
        height: 'height',
        'border-radius': 'borderRadius',
    }

    if (styleTargets[event.target]) {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            style: {
                ...(elementForEdit.value.extra?.style ?? {}),
                [styleTargets[event.target]]: event.value,
            },
        }
    }

    if (['style_width_lock', 'style_height_lock', 'style_border_radius_lock'].includes(event.target)) {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [event.target]: event.value,
        }
    }

    betaStore.updateElementForEdit()
}

const onChangeSpacing = (event: any, target: 'margin' | 'padding') => {
    if (!elementForEdit.value) return

    if (event.config === 'desktop') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [target]: {
                ...elementForEdit.value.extra?.[target],
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'mobile') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [`${target}_mobile`]: {
                ...elementForEdit.value.extra?.[`${target}_mobile`],
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'lock') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [`${target}_mobile_lock`]: event.value,
        }
    }

    betaStore.updateElementForEdit()
}
</script>

<template>
    <editor-sidebar-layout v-if="elementForEdit" :settings="settings">
        <template #image>
            <settings-layout name="Configurações da Imagem" :default-open="true">
                <setting-image-selector
                    label="Imagem"
                    :value="elementForEdit.frameContent"
                    @change="onChangeImageSelector"
                />

                <setting-link
                    label="Link"
                    type="number"
                    :value="elementForEdit.action"
                    :value-open-in="elementForEdit.actionType"
                    @change="onChangeLink"
                />

                <setting-image-width
                    label="Largura da Imagem"
                    :value="imageStyle.width"
                    :lock="imageStyleWidthLock"
                    @change="onChangeStyle"
                />

                <setting-image-height
                    label="Altura da Imagem"
                    :value="imageStyle.height"
                    :lock="imageStyleHeightLock"
                    @change="onChangeStyle"
                />

                <setting-border-radius
                    label="Borda Arredondada"
                    :value="imageStyle.borderRadius"
                    :lock="imageStyleBorderRadiusLock"
                    @change="onChangeStyle"
                />
            </settings-layout>
        </template>

        <template #layout>
            <settings-layout name="Configurações do Conteúdo" :default-open="true">
                <setting-width
                    label="Largura"
                    :value="elementForEdit.grid"
                    :value-mobile="elementForEdit.gridMobile"
                    :values-permission-desktop="authStore.accessType == 'admin' ? [3, 4, 6, 12, 99] : [4, 6, 12, 99]"
                    :values-permission-mobile="[4, 6, 12, 99]"
                    @change="onChangeWidth"
                />
            </settings-layout>

            <settings-layout name="Avançado">
                <setting-margin
                    label="Margem Externa"
                    :value="elementForEdit.extra?.margin"
                    :value-mobile="elementForEdit.extra?.margin_mobile"
                    :lock="elementForEdit.extra?.margin_mobile_lock"
                    @change="(event: any) => onChangeSpacing(event, 'margin')"
                />

                <setting-padding
                    label="Margem Interna"
                    :value="elementForEdit.extra?.padding"
                    :value-mobile="elementForEdit.extra?.padding_mobile"
                    :lock="elementForEdit.extra?.padding_mobile_lock"
                    @change="(event: any) => onChangeSpacing(event, 'padding')"
                />
            </settings-layout>
        </template>
    </editor-sidebar-layout>
</template>
